'use client'

import { useAuthorization } from '@entities/authentication'
import { rpc } from '@server/infrastructure/rpc'
import { withRequestTimeout as withNpsRequestTimeout } from '@shared/apis/with-request-timeout'
import { useTranslation } from '@shared/config/i18n'
import { useToast } from '@shared/hooks'
import { useMutation } from '@tanstack/react-query'

import { skipNps } from '../api/nps-direct.api'
import type {
  NpsSkipPayload,
  NpsSubmitPayload,
  NpsUpdateFeedbackPayload,
  NpsUpdateWantBlockPayload,
} from '../ui/nps-survey/nps-survey.types'

const readSavedNpsResponse = async (response: Pick<Response, 'ok' | 'status' | 'json'>): Promise<{ success: true }> => {
  if (!response.ok) {
    throw new Error(`Failed to save NPS: ${response.status}`)
  }
  const result = await response.json()
  if (result?.success !== true) {
    throw new Error('NPS was not saved')
  }
  return result
}

export const useNpsSubmit = () => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['nps', 'submit', bearerToken],
    mutationFn: async (payload: NpsSubmitPayload) => {
      return await withNpsRequestTimeout(async (signal) => {
        const res = await rpc.api.v1['lesson-review'].nps.$post(
          { json: { ...payload, source: payload.source ?? 'IN_FLOW' } },
          { headers: { Authorization: `Bearer ${bearerToken}` }, init: { signal } },
        )
        return await readSavedNpsResponse(res)
      })
    },
    onError: () => {
      toast({ title: t('lessonReview.nps.errors.submitFailed') })
    },
  })
}

export const useNpsSkip = () => {
  const { bearerToken } = useAuthorization()

  return useMutation({
    mutationKey: ['nps', 'skip', bearerToken],
    mutationFn: async (payload: NpsSkipPayload) => {
      return await skipNps(bearerToken, payload)
    },
  })
}

export const useNpsUpdateFeedback = () => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['nps', 'updateFeedback', bearerToken],
    mutationFn: async (payload: NpsUpdateFeedbackPayload) => {
      return await withNpsRequestTimeout(async (signal) => {
        const res = await rpc.api.v1['lesson-review'].nps.feedback.$patch(
          { json: payload },
          { headers: { Authorization: `Bearer ${bearerToken}` }, init: { signal } },
        )
        return await readSavedNpsResponse(res)
      })
    },
    onError: () => {
      toast({ title: t('lessonReview.nps.errors.submitFailed') })
    },
  })
}

export const useNpsUpdateWantBlock = () => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['nps', 'updateWantBlock', bearerToken],
    mutationFn: async (payload: NpsUpdateWantBlockPayload) => {
      return await withNpsRequestTimeout(async (signal) => {
        const res = await rpc.api.v1['lesson-review'].nps['want-block'].$patch(
          { json: payload },
          { headers: { Authorization: `Bearer ${bearerToken}` }, init: { signal } },
        )
        return await readSavedNpsResponse(res)
      })
    },
    onError: () => {
      toast({ title: t('lessonReview.nps.errors.submitFailed') })
    },
  })
}
